package com.schoolconnect.app.feature.attendance

import com.schoolconnect.app.designsystem.appDateInputComparable
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.compareBySmartText

internal fun attendanceDateValidationError(value: String, today: String): String {
    if (value.isBlank()) return "Attendance date is required."
    val dateValue = appDateInputComparable(value)
        ?: return "Enter a valid attendance date in DD MMM YYYY format."
    val todayValue = appDateInputComparable(today)
        ?: return "Unable to validate the attendance date. Try again."
    return if (dateValue > todayValue) "Attendance date cannot be in the future." else ""
}

data class AttendanceFilterValues(
    val className: String = "",
    val classSectionId: String = "",
    val date: String = "",
    val status: String = "",
    val timePeriod: String = "Overall",
    val performance: String = ""
)

data class AttendanceRowUi(
    val key: String,
    val studentId: String = "",
    val rollNumber: String,
    val studentName: String,
    val classLabel: String,
    val status: String,
    val remarks: String,
    val attendanceId: String = ""
)

data class AttendanceClassOption(val id: String, val label: String)

data class QuickMarkExceptionUi(val studentId: String, val status: String, val remarks: String?)

data class QuickMarkSubmission(
    val classSectionId: String,
    val attendanceDate: String,
    val exceptions: List<QuickMarkExceptionUi>
)

data class AttendanceInsightRowUi(
    val key: String,
    val studentId: String = "",
    val rollNumber: String,
    val studentName: String,
    val classLabel: String,
    val attendancePercentage: Int,
    val totalDays: Int,
    val presentDays: Int,
    val absentDays: Int,
    val lateDays: Int,
    val excusedDays: Int,
    val performance: String
)

data class AttendanceUiState(
    val isLoading: Boolean = false,
    val errorMessage: String = "",
    val saveErrorMessage: String = "",
    val searchQuery: String = "",
    val filters: AttendanceFilterValues = AttendanceFilterValues(),
    val rows: List<AttendanceRowUi> = emptyList(),
    val attendanceRosterRows: List<AttendanceRowUi> = emptyList(),
    val quickMarkRosterRows: List<AttendanceRowUi> = emptyList(),
    val isQuickMarkRosterLoading: Boolean = false,
    val isQuickMarkAttendanceLoading: Boolean = false,
    val quickMarkRosterError: String = "",
    val totalCount: Int = 0,
    val availableClasses: List<AttendanceClassOption> = emptyList(),
    val classFilterLabel: String = "All My Classes",
    val insightRows: List<AttendanceInsightRowUi> = emptyList(),
    val insightTotal: Int = 0,
    val insightAverageValue: Int = 0,
    val insightGoodCount: Int = 0,
    val insightAveragePerformanceCount: Int = 0,
    val insightNeedsAttentionCount: Int = 0,
    val insightStudentCount: Int = 0,
    val isQuickMarkSaving: Boolean = false,
    val quickMarkSaveCompleted: Boolean = false,
    val isAttendanceSaving: Boolean = false,
    val attendanceEditCompleted: Boolean = false,
    val isInsightsLoading: Boolean = false,
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val selectedTab: AttendanceTab = AttendanceTab.MARK_ATTENDANCE,
    val sortField: AttendanceSortField = AttendanceSortField.ROLL_NUMBER,
    val sortAscending: Boolean = true
) {
    val totalStudents: Int get() = totalCount
    val presentCount: Int get() = rows.count { it.status == "P" }
    val absentCount: Int get() = rows.count { it.status == "A" }
    val halfDayAmCount: Int get() = rows.count { it.status == "A-M" }
    val halfDayAaCount: Int get() = rows.count { it.status == "A-A" }
    val classOptions: List<String> get() = availableClasses.map { it.label }.distinct().sortedWith(compareBySmartText { it })
    val insightClassOptions: List<String> get() = availableClasses.map { it.label }.distinct().sortedWith(compareBySmartText { it })
    val insightAverage: Int get() = insightAverageValue
    val goodPerformanceCount: Int get() = insightGoodCount
    val averagePerformanceCount: Int get() = insightAveragePerformanceCount
    val needsAttentionCount: Int get() = insightNeedsAttentionCount
}

enum class AttendanceTab { MARK_ATTENDANCE, INSIGHTS }

sealed interface AttendanceUiEvent {
    data object BackClicked : AttendanceUiEvent
    data object RefreshClicked : AttendanceUiEvent
    data class SearchChanged(val query: String) : AttendanceUiEvent
    data class FiltersChanged(val filters: AttendanceFilterValues) : AttendanceUiEvent
    data object FiltersReset : AttendanceUiEvent
    data class PageChanged(val pageIndex: Int) : AttendanceUiEvent
    data class PageSizeChanged(val pageSize: Int) : AttendanceUiEvent
    data class TabChanged(val tab: AttendanceTab) : AttendanceUiEvent
    data class StatusChanged(val rowKey: String, val status: String) : AttendanceUiEvent
    data class AttendanceEdited(val rowKey: String, val date: String, val status: String, val remarks: String) : AttendanceUiEvent
    data object AttendanceEditAcknowledged : AttendanceUiEvent
    data object QuickMarkClicked : AttendanceUiEvent
    data class QuickMarkContextChanged(val classSectionId: String, val attendanceDate: String) : AttendanceUiEvent
    data class QuickMarkSaved(val submission: QuickMarkSubmission) : AttendanceUiEvent
    data object QuickMarkSaveAcknowledged : AttendanceUiEvent
    data class SortChanged(val field: AttendanceSortField) : AttendanceUiEvent
}

enum class AttendanceSortField(val apiField: String) {
    ROLL_NUMBER("roll_number"),
    STUDENT_NAME("student_name"),
    CLASS("class_name"),
    STATUS("status"),
    REMARKS("remarks"),
    ATTENDANCE("attendance_percentage"),
    PRESENT_DAYS("present_days"),
    ABSENT_DAYS("absent_days"),
    PERFORMANCE("performance_band")
}
